import React, { useEffect, useMemo, useState } from "react";
import {
  FiltersRow,
  FilterGroup,
  FilterLabel,
  FilterSelect,
  InlineInput,
  BreachWrapper,
  BreachTableWrapper,
  BreachTable,
  BreachThead,
  BreachTh,
  BreachTr,
  BreachTd,
  BreachKpiTd,
  BreachStatusPill,
  BreachEmptyState,
  BreachEmptyCell,
} from "../styles";
import { PERIOD_TYPE_LABELS, formatDateRange } from "../hooks/useTriggers";

const STATUS_LABELS = { ACTIVE: "Active", RESOLVED: "Resolved" };

const formatDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const BreachedTriggersTab = ({ hook }) => {
  const {
    selectedFinancialYear,
    computePeriodSlots,
    breachedTriggers,
    loadingBreachedTriggers,
  } = hook;

  // Filters
  const [kpiQuery, setKpiQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodType, setPeriodType] = useState("");
  const [periodSlot, setPeriodSlot] = useState(""); // "fromDate|toDate"
  const [locationFilter, setLocationFilter] = useState("");

  // Reset filters on FY change
  useEffect(() => {
    setKpiQuery("");
    setStatusFilter("");
    setPeriodType("");
    setPeriodSlot("");
    setLocationFilter("");
  }, [selectedFinancialYear]);

  // Period types that actually have breaches in this FY
  const availablePeriodTypes = useMemo(() => {
    const types = ["YEARLY", "HALF_YEARLY", "QUARTERLY", "MONTHLY"];
    return types.filter((type) => {
      const slots = computePeriodSlots(type);
      return slots.some((slot) =>
        breachedTriggers.some((b) => b.fromDate === slot.fromDate && b.toDate === slot.toDate)
      );
    });
  }, [breachedTriggers, computePeriodSlots]);

  // Period slots for the selected period type that have at least one breach
  const availablePeriodSlots = useMemo(() => {
    if (!periodType) return [];
    const slots = computePeriodSlots(periodType);
    return slots.filter((slot) =>
      breachedTriggers.some((b) => b.fromDate === slot.fromDate && b.toDate === slot.toDate)
    );
  }, [periodType, breachedTriggers, computePeriodSlots]);

  // Constrain breaches by period type before computing other dropdowns
  const periodTypeFiltered = useMemo(() => {
    if (!periodType) return breachedTriggers;
    const keys = new Set(availablePeriodSlots.map((s) => `${s.fromDate}|${s.toDate}`));
    return breachedTriggers.filter((b) => keys.has(`${b.fromDate}|${b.toDate}`));
  }, [breachedTriggers, periodType, availablePeriodSlots]);

  const statusOptions = useMemo(() => {
    return [...new Set(periodTypeFiltered.map((b) => b.status).filter(Boolean))];
  }, [periodTypeFiltered]);

  const locationOptions = useMemo(() => {
    return [...new Set(periodTypeFiltered.map((b) => b.location).filter(Boolean))].sort();
  }, [periodTypeFiltered]);

  const filtered = useMemo(() => {
    const q = kpiQuery.trim().toLowerCase();
    return periodTypeFiltered.filter((b) => {
      if (q && !(b.kpi ?? "").toLowerCase().includes(q)) return false;
      if (statusFilter && b.status !== statusFilter) return false;
      if (periodSlot && `${b.fromDate}|${b.toDate}` !== periodSlot) return false;
      if (locationFilter && b.location !== locationFilter) return false;
      return true;
    });
  }, [periodTypeFiltered, kpiQuery, statusFilter, periodSlot, locationFilter]);

  const handlePeriodTypeChange = (e) => {
    setPeriodType(e.target.value);
    setPeriodSlot("");
  };

  return (
    <BreachWrapper>
      <FiltersRow>
        <FilterGroup style={{ flex: "1 1 240px", minWidth: 200 }}>
          <FilterLabel>Search KPI</FilterLabel>
          <InlineInput
            type="text"
            placeholder="Search by KPI name..."
            value={kpiQuery}
            onChange={(e) => setKpiQuery(e.target.value)}
            disabled={!selectedFinancialYear}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={!selectedFinancialYear || statusOptions.length === 0}
          >
            <option value="">All statuses</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>
            ))}
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Period Type</FilterLabel>
          <FilterSelect
            value={periodType}
            onChange={handlePeriodTypeChange}
            disabled={!selectedFinancialYear || availablePeriodTypes.length === 0}
          >
            <option value="">All types</option>
            {availablePeriodTypes.map((type) => (
              <option key={type} value={type}>{PERIOD_TYPE_LABELS[type]}</option>
            ))}
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Period</FilterLabel>
          <FilterSelect
            value={periodSlot}
            onChange={(e) => setPeriodSlot(e.target.value)}
            disabled={!periodType || availablePeriodSlots.length === 0}
          >
            <option value="">All periods</option>
            {availablePeriodSlots.map((slot) => (
              <option key={slot.fromDate} value={`${slot.fromDate}|${slot.toDate}`}>
                {slot.label}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Location</FilterLabel>
          <FilterSelect
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            disabled={!selectedFinancialYear || locationOptions.length === 0}
          >
            <option value="">All locations</option>
            {locationOptions.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </FilterSelect>
        </FilterGroup>
      </FiltersRow>

      {!selectedFinancialYear ? (
        <BreachEmptyState style={{ marginTop: 40 }}>
          Select a financial year to view breached triggers.
        </BreachEmptyState>
      ) : (
        <BreachTableWrapper>
          <BreachTable>
            <BreachThead>
              <tr>
                <BreachTh>Status</BreachTh>
                <BreachTh>Period</BreachTh>
                <BreachTh>Location</BreachTh>
                <BreachTh>KPI</BreachTh>
                <BreachTh>Current Value</BreachTh>
                <BreachTh>Min Threshold</BreachTh>
                <BreachTh>Max Threshold</BreachTh>
                <BreachTh>Unit</BreachTh>
                <BreachTh>Detected At</BreachTh>
              </tr>
            </BreachThead>
            <tbody>
              {loadingBreachedTriggers ? (
                <tr>
                  <td colSpan={9}><BreachEmptyState>Loading...</BreachEmptyState></td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <BreachEmptyState>No breached triggers found for the selected filters.</BreachEmptyState>
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <BreachTr key={b.id}>
                    <BreachTd><BreachStatusPill $kind={b.status}>{STATUS_LABELS[b.status] ?? b.status}</BreachStatusPill></BreachTd>
                    <BreachTd>{formatDateRange(b.fromDate, b.toDate)}</BreachTd>
                    <BreachTd>{b.location || <BreachEmptyCell>—</BreachEmptyCell>}</BreachTd>
                    <BreachKpiTd>{b.kpi || <BreachEmptyCell>—</BreachEmptyCell>}</BreachKpiTd>
                    <BreachTd>{b.currentValue ?? <BreachEmptyCell>—</BreachEmptyCell>}</BreachTd>
                    <BreachTd>{b.minThreshold ?? <BreachEmptyCell>—</BreachEmptyCell>}</BreachTd>
                    <BreachTd>{b.maxThreshold ?? <BreachEmptyCell>—</BreachEmptyCell>}</BreachTd>
                    <BreachTd>{b.unit || <BreachEmptyCell>—</BreachEmptyCell>}</BreachTd>
                    <BreachTd>{formatDateTime(b.occurredAt)}</BreachTd>
                  </BreachTr>
                ))
              )}
            </tbody>
          </BreachTable>
        </BreachTableWrapper>
      )}
    </BreachWrapper>
  );
};

export default BreachedTriggersTab;
